import { formatClockTime } from '@/shared/lib/time-format';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Calendar, Check, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Segmented, SelectionGroup } from '@scripsteam/scrips-signal-ds';
import { BridgeStatusChip, type StatusChipProps } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/auth/auth-store';
import { useRetrieveEndDate } from '@/services/scheduling/use-retrieve-end-date';
import {
  useRetrieveSlots,
  type SlotsDataModel,
  type SlotDetail,
} from '@/services/scheduling/use-retrieve-slots';
import { useRetrieveResourceSlots } from '@/scheduling/services/use-retrieve-resource-slots';
import type { AppointmentSlot, AppointmentValueSets } from '@/types/scheduling-api';
import type { AppointmentFormValues } from '@/types/appointment-form';
import {
  SLOT_STATUS_AVAILABLE,
  SLOT_STATUS_SKIPPED,
  effectiveSlotStatus,
  isDoctorChanged,
  computeSaveEnabled,
  countUnresolved,
  countActiveOccurrences,
  applyPickSuggested,
  applySkip,
  applyUndo,
  applyEdit,
  applyReselect,
} from './recurrence-conflict';
import { RecurrenceReselectTimeView } from './recurrence-reselect-time-view';
import { RecurrenceReselectDoctorView } from './recurrence-reselect-doctor-view';

/**
 * WHOSE calendar the recurrence is checked against (PROD-2383).
 *
 * `provider` — the create rail's practitioner, read from the form
 * (`providerId` / `appointmentProfileId`), checked through
 * `POST /api/Appointment/RetrieveSlots`. The default, and unchanged.
 *
 * `device` — a machine and its product, checked through the device mirror
 * `POST /api/Appointment/RetrieveResourceSlots`. Ruling 2026-09-22: "they
 * should be mirror of the one for the provider", so it is the SAME
 * configurator — steppers, period, weekdays, end date, the conflict list with
 * Pick / Skip / Undo / Edit — and differs in one place only: the per-occurrence
 * RE-SELECTION views are not offered. "Select another doctor" is meaningless
 * for a device (you do not swap the chamber mid-course), and "See all" browses
 * a PRACTITIONER's time grid (`RecurrenceReselectTimeView` is provider-keyed),
 * so offering it would show a doctor's free times for a machine. A device
 * occurrence is resolved by one of the server's suggested device windows, or
 * skipped.
 */
export type RecurrenceActor =
  | { kind: 'provider' }
  | {
      kind: 'device';
      deviceId: string;
      deviceProfileId: string;
      /** The practice the device stands in — the device rail's own, not read from the session. */
      practiceId: string;
    };

interface RecurrencePickerViewProps {
  /** Closes the view; when `commit` is true the picker writes its in-flight
   * recurrence + slots to the form before closing (Flutter Save). When
   * false the form's `recurring` is reverted (Flutter Cancel — note that
   * Flutter only reverts when the recurrence wasn't already in edit mode). */
  onClose: (commit?: boolean) => void;
  /** PROD-2383 — whose calendar to check. Defaults to the form's provider. */
  actor?: RecurrenceActor;
}

const PERIODS = ['Days', 'Weeks', 'Months'] as const;
type Period = (typeof PERIODS)[number];

/** Full weekday names — backend accepts these verbatim. */
const WEEKDAYS = [
  { value: 'Monday', label: 'M' },
  { value: 'Tuesday', label: 'T' },
  { value: 'Wednesday', label: 'W' },
  { value: 'Thursday', label: 'T' },
  { value: 'Friday', label: 'F' },
  { value: 'Saturday', label: 'S' },
  { value: 'Sunday', label: 'S' },
];

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTH_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

function fmtTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatClockTime(d);
}

function weekdayName(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
}

/**
 * RecurrencePickerView — full panel-content replacement for configuring
 * a recurring appointment. Mirrors Flutter
 * `recurrence_appointment/recurrence_appointment_widget.dart`:
 *   - Top bar: Back / Recurrence / Save (gated on availability)
 *   - "Start on …" header (read-only, from the picked slot)
 *   - Repeat-every stepper, Period chips (Days/Weeks/Months)
 *   - Weekday toggles (when period === 'Weeks')
 *   - Number-of-occurrences stepper
 *   - "End on …" computed live via POST /api/Appointment/RetrieveEndDate
 *   - "Check availability" button hits POST /api/Appointment/RetrieveSlots
 *     and renders an "All slots available" banner or a per-occurrence
 *     conflict-resolution list (PROD-932): each unavailable occurrence can
 *     be resolved by picking a backend-suggested slot or skipping it; a
 *     resolved occurrence can be Edited (reverted) and a skipped one Undone.
 *     Save unlocks once no occurrence is left "Not available"
 *     (scheduling_bloc.dart:1264-1277). Validation failures on Check surface
 *     verbatim Flutter toasts (PROD-933).
 *
 * Per-occurrence re-selection (PROD-1011): "See all" re-enters slot selection
 * for the SAME doctor (RecurrenceReselectTimeView); "Select another doctor"
 * re-enters provider selection (RecurrenceReselectDoctorView). Both render as
 * isolated sub-views (local `reselect` state) that reuse the create-flow
 * hooks but return the pick via callback — they never write the main
 * appointment form (mirrors Flutter's `select_doctor_widget` own internalBloc).
 * The picked slot carries the resolved providerId, so a re-assigned occurrence
 * books under the new doctor (Book payload unchanged — PROD-961).
 */
export function RecurrencePickerView({ onClose, actor = { kind: 'provider' } }: RecurrencePickerViewProps) {
  const device = actor.kind === 'device' ? actor : null;
  const formApi = useFormContext<AppointmentFormValues>();
  const { watch } = formApi;
  const slotStart = watch('slotStart');
  const slotEnd = watch('slotEnd');
  const minutesDuration = watch('minutesDuration');
  const location = watch('location');
  const providerId = watch('providerId');
  const providerName = watch('providerName');
  const appointmentProfileId = watch('appointmentProfileId');
  const appointmentType = watch('appointmentType');
  const existing = watch('recurring');

  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';
  const practiceName = useAuthStore((s) => s.user?.practiceName) ?? null;
  const organizationId = useAuthStore((s) => s.user?.organizationId) ?? '';

  // PROD-1011 — per-occurrence re-selection sub-view. When set, the picker
  // renders the doctor / time re-selection instead of the conflict list.
  const [reselect, setReselect] = useState<{
    index: number;
    mode: 'doctor' | 'time';
  } | null>(null);

  // Local state — hydrated from form on mount, never auto-synced after.
  const [repeatEvery, setRepeatEvery] = useState<number>(existing?.repeatEvery ?? 1);
  const [numberOfRepeat, setNumberOfRepeat] = useState<number>(existing?.numberOfRepeat ?? 1);
  const [period, setPeriod] = useState<Period>(
    (existing?.period as Period | undefined) && PERIODS.includes(existing!.period as Period)
      ? (existing!.period as Period)
      : 'Days',
  );

  // Default the weekday selection to the slot's actual weekday when the
  // user picks "Weeks" without ever specifying days (Flutter setStartWeekDay).
  const defaultDays = useMemo(() => {
    const wd = weekdayName(slotStart ?? undefined);
    return wd ? [wd] : [];
  }, [slotStart]);
  const [days, setDays] = useState<string[]>(existing?.days ?? defaultDays);

  // End date is derived from the backend; cache it so the "End on …" row
  // can show stale-but-rendered values while a refetch is in flight.
  const [endDate, setEndDate] = useState<string | null>(
    existing?.endDate ?? slotEnd ?? null,
  );
  const [slotsData, setSlotsData] = useState<SlotsDataModel | null>(null);

  const retrieveEndDate = useRetrieveEndDate();
  const retrieveSlots = useRetrieveSlots();
  const retrieveResourceSlots = useRetrieveResourceSlots();
  const checking = retrieveSlots.isPending || retrieveResourceSlots.isPending;

  // Whenever a user-driven recurrence input changes, refresh the end date and
  // invalidate any prior availability check (the shape changed → re-check).
  // Every dep here is a genuine user input (steppers / period chips / weekday
  // toggles / the picked slot). Skip/undo deliberately do NOT mutate any of
  // them — they only edit `slotsData` — so this effect can never fire mid-
  // resolution and wipe the conflict list (PR #383 review — Samer #2). The
  // booked occurrence count is derived from `slotsData` at save time instead.
  useEffect(() => {
    if (!slotStart) return;
    if (period === 'Weeks' && days.length === 0) return;
    retrieveEndDate.mutate(
      {
        startDate: slotStart,
        repeatEvery,
        numberOfRepeat,
        period,
        days,
      },
      { onSuccess: (iso) => setEndDate(iso) },
    );
    // Reset availability check whenever the recurrence shape changes — this is
    // a deliberate invalidation of stale server state, not a derived value.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSlotsData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotStart, repeatEvery, numberOfRepeat, period, days.join(',')]);

  const checkAvailability = () => {
    // Prereqs are gated upstream (the Recurrence button only enables once
    // provider + profile + slot are set) — keep these as silent guards,
    // Flutter doesn't toast for them either.
    if (!slotStart || !endDate) return;
    if (device ? !device.deviceId || !device.deviceProfileId : !providerId || !appointmentProfileId) return;

    // PROD-933 — validation toasts, verbatim from Flutter
    // `recurrence_appointment_widget.dart:266-294`, fired in the same order.
    if (repeatEvery < 1) {
      toast.error('Please select repeat every');
      return;
    }
    if (!period) {
      toast.error('Please select period');
      return;
    }
    if (period === 'Weeks' && days.length === 0) {
      toast.error('Please select weekdays');
      return;
    }
    if (numberOfRepeat < 1) {
      toast.error('Please select number of repeat');
      return;
    }

    if (device) {
      retrieveResourceSlots.mutate(
        {
          deviceId: device.deviceId,
          deviceProfileId: device.deviceProfileId,
          practiceId: device.practiceId,
          startDate: slotStart,
          repeatEvery,
          numberOfRepeat,
          period,
          days,
        },
        { onSuccess: (data) => setSlotsData(data) },
      );
      return;
    }

    retrieveSlots.mutate(
      {
        providerId: providerId ?? '',
        practiceId,
        appointmentProfileId: appointmentProfileId ?? '',
        startDate: slotStart,
        endDate,
        repeatEvery,
        numberOfRepeat,
        period,
        days,
      },
      { onSuccess: (data) => setSlotsData(data) },
    );
  };

  // Green "All slots available" banner uses the server's original flag; it
  // stays false after inline resolution (mirrors Flutter — the banner only
  // shows on a clean first check, while Save unlocks via computeSaveEnabled).
  const allAvailable = !!slotsData?.allSlotsAvailable;
  const unresolvedCount = countUnresolved(slotsData);
  const saveEnabled = computeSaveEnabled(slotsData) && !!slotStart && !!endDate;

  // --- PROD-932 inline conflict-resolution handlers (operate on the cached
  // slotsData; no re-fetch — the suggestions came back with the check). ---

  const pickSuggested = (index: number, slot: AppointmentSlot) => {
    if (!slotsData) return;
    setSlotsData(applyPickSuggested(slotsData, index, slot));
  };

  // Skip / undo only edit `slotsData`. Flutter live-decrements the
  // numberOfRepeat stepper on skip (widget 789-795 / 1080-1085); we keep the
  // stepper at the user's configured value and instead derive the booked
  // count from the resolved `slotsData` at save time (see onSave) — same wire
  // value (`slots.length`), without mutating an effect-watched input. The
  // conflict summary surfaces how many will actually book.
  const skip = (index: number) => {
    if (!slotsData) return;
    setSlotsData(applySkip(slotsData, index));
  };

  const undo = (index: number) => {
    if (!slotsData) return;
    setSlotsData(applyUndo(slotsData, index));
  };

  const edit = (index: number) => {
    if (!slotsData) return;
    // Note: Flutter restores `oldEndDate` here. We intentionally keep the
    // "End on …" value stable through inline resolution — the per-occurrence
    // `slots` array (sent on Save) is authoritative for the booked times, and
    // Flutter's single-level oldEndDate undo is fuzzy for multi-conflict sets.
    setSlotsData(applyEdit(slotsData, index));
  };

  // PROD-1011 — open the re-selection sub-views.
  const openSeeAll = (index: number) => setReselect({ index, mode: 'time' });
  const openSelectDoctor = (index: number) => setReselect({ index, mode: 'doctor' });

  // "See all" — same doctor, new time. Doctor unchanged.
  const onReselectTimePick = (slot: AppointmentSlot) => {
    if (!slotsData || reselect == null) return;
    setSlotsData(applyReselect(slotsData, reselect.index, { slot, doctorChanged: false }));
    setReselect(null);
  };

  // "Select another doctor" — flag doctorChanged when the picked provider
  // differs from the appointment's original provider (drives the
  // "Doctor has been changed." note + ships the new providerId on the slot).
  const onReselectDoctorPick = (
    doctor: { id: string; name: string },
    slot: AppointmentSlot,
  ) => {
    if (!slotsData || reselect == null) return;
    const doctorChanged = doctor.id !== providerId;
    setSlotsData(
      applyReselect(slotsData, reselect.index, {
        slot,
        doctor: doctorChanged ? doctor : undefined,
        doctorChanged,
      }),
    );
    setReselect(null);
  };

  const onSave = () => {
    if (!slotStart || !endDate || !slotsData) return;
    // Forward the FULL AppointmentSlot shape (Flutter parity —
    // recurrence_properties_model.dart:108-110). Reducing to
    // `{ start, end }` previously dropped practiceId / providerId /
    // overbooked / appointmentType / comment / practiceName — the
    // backend's ConvertCreateAppointmentCommandTimeToUtc dereferences
    // those during UTC conversion and NPEs when they're absent
    // (PROD-961).
    const slots = (slotsData.slotDetails ?? [])
      .map((d) => d.slot ?? d.selectedSlot)
      .filter((s): s is NonNullable<typeof s> => !!s)
      .map((s) => ({
        appointmentType: s.appointmentType ?? null,
        start: s.start,
        end: s.end,
        comment: s.comment ?? null,
        overbooked: s.overbooked ?? false,
        practiceId: s.practiceId ?? '00000000-0000-0000-0000-000000000000',
        providerId: s.providerId,
        practiceName: s.practiceName ?? null,
      }));
    // Wire `numberOfRepeat` = the count that actually books, i.e. the
    // resolved-and-not-skipped occurrences. This equals `slots.length` by
    // construction (a skipped occurrence has neither `slot` nor
    // `selectedSlot`, so it's filtered above) and matches Flutter, which
    // live-decrements `recurrenceProperties.numberOfRepeat` on each skip and
    // ships that value on Book.
    const bookedCount = countActiveOccurrences(slotsData);
    const { setValue } = formApi;
    setValue(
      'recurring',
      {
        startDate: slotStart,
        endDate,
        repeatEvery,
        numberOfRepeat: bookedCount,
        period,
        // Flutter ships `days` only when period === 'Weeks';
        // Days/Months/Years go out as an empty array here and the
        // transform translates that to `null` on the wire
        // (recurrence_properties_model.dart:105 mirror).
        days: period === 'Weeks' ? days : [],
        slots,
      },
      { shouldValidate: true },
    );
    onClose(true);
  };

  // PROD-1011 — per-occurrence re-selection sub-views (rendered in place of
  // the conflict list). Isolated from the main appointment form (Flutter's
  // select_doctor_widget / recurrence date-time picker run their own state).
  if (reselect != null) {
    const occ = slotsData?.slotDetails?.[reselect.index];
    const occDate = occ?.appointmentTime ?? slotStart ?? '';
    const profileType: 'ROUTINE' | 'FOLLOWUP' | null =
      appointmentType?.code === 'FOLLOWUP'
        ? 'FOLLOWUP'
        : appointmentType?.code === 'ROUTINE'
          ? 'ROUTINE'
          : null;
    // Normalise the form's coding (definition: string | null) into the
    // AppointmentValueSets shape the slot carries (definition: string | undefined).
    const apptType: AppointmentValueSets | undefined = appointmentType
      ? {
          code: appointmentType.code,
          display: appointmentType.display,
          definition: appointmentType.definition ?? undefined,
        }
      : undefined;
    if (reselect.mode === 'time') {
      return (
        <RecurrenceReselectTimeView
          providerId={providerId ?? ''}
          providerName={providerName ?? undefined}
          practiceId={practiceId}
          practiceName={practiceName}
          appointmentProfileId={appointmentProfileId ?? undefined}
          appointmentType={apptType}
          initialDate={occDate}
          onPick={onReselectTimePick}
          onCancel={() => setReselect(null)}
        />
      );
    }
    return (
      <RecurrenceReselectDoctorView
        organizationId={organizationId}
        practiceId={practiceId}
        practiceName={practiceName}
        locationIds={location?.id ? [location.id] : []}
        appointmentProfileType={profileType}
        appointmentType={apptType}
        initialDate={occDate}
        onPick={onReselectDoctorPick}
        onCancel={() => setReselect(null)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default flex-shrink-0">
        <button
          onClick={() => onClose(false)}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="text-base font-bold text-text-primary">Recurrence</h2>
        <button
          type="button"
          onClick={onSave}
          disabled={!saveEnabled}
          className={cn(
            'text-sm font-medium',
            saveEnabled
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          Save
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-4">
          {/* Start on row */}
          <div className="flex items-start gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
            <Calendar className="h-5 w-5 text-brand-primary flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary leading-snug">
                Start on {fmtDate(slotStart ?? undefined)} at {fmtTime(slotStart ?? undefined)}
              </p>
              <p className="text-xs text-text-muted leading-snug">
                {minutesDuration ? `${minutesDuration} minutes` : ''}
                {minutesDuration && location?.name ? ' · ' : ''}
                {location?.name ?? ''}
              </p>
            </div>
          </div>

          {/* Repeat every */}
          <Stepper
            label="Repeat every"
            value={repeatEvery}
            min={1}
            onChange={setRepeatEvery}
          />

          {/* Period — Signal Segmented (exclusive choice). Was a hand-rolled
              button group; Segmented encodes the selected-segment visual +
              role="radiogroup" a11y. Labels singularised (Day/Week/Month) to
              match the prior copy. */}
          <div>
            <p className="text-xs font-medium text-text-muted mb-2">
              Period
            </p>
            <Segmented
              aria-label="Recurrence period"
              options={PERIODS.map((p) => ({ label: p.replace(/s$/, ''), value: p }))}
              value={period}
              onChange={(v) => setPeriod(v as Period)}
            />
          </div>

          {/* Weekday toggles — Signal SelectionGroup (multi-select). Only when
              period === 'Weeks'. Append-on-toggle order matches the prior
              hand-rolled behaviour; wire only cares about membership. */}
          {period === 'Weeks' ? (
            <div>
              <p className="text-xs font-medium text-text-muted mb-2">
                Repeat on
              </p>
              <SelectionGroup
                aria-label="Repeat on"
                options={WEEKDAYS}
                value={days}
                onChange={(next) => setDays(next)}
              />
            </div>
          ) : null}

          {period === 'Months' ? (
            <p className="text-xs text-text-muted leading-snug">
              Every 4 weeks (exactly 28 days).
            </p>
          ) : null}

          {/* Number of occurrences */}
          <Stepper
            label="Ends after 'number' of occurrences"
            value={numberOfRepeat}
            min={1}
            onChange={setNumberOfRepeat}
          />

          {/* End on row */}
          <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
            <Calendar className="h-5 w-5 text-brand-primary flex-shrink-0" />
            <p className="flex-1 text-sm font-medium text-text-primary leading-snug">
              End on {fmtDate(endDate ?? undefined)} at {fmtTime(slotStart ?? undefined)}
              {retrieveEndDate.isPending ? ' · …' : ''}
            </p>
          </div>

          {/* Check availability / conflict resolution */}
          {allAvailable ? (
            <div className="flex items-center justify-center gap-2 h-12 rounded-lg bg-green-100 text-green-700">
              <Check className="h-4 w-4" />
              <span className="text-sm font-semibold">All Slots are available</span>
            </div>
          ) : !slotsData ? (
            // Not disabled for empty weekdays — Flutter keeps the button live
            // and toasts on press (PROD-933). Only disabled while a check runs.
            <button
              type="button"
              onClick={checkAvailability}
              disabled={checking}
              className={cn(
                'w-full h-12 rounded-lg text-sm font-semibold transition-colors',
                checking
                  ? 'bg-grey-100 text-text-hint cursor-not-allowed'
                  : 'bg-grey-100 text-brand-primary hover:bg-blue-100',
              )}
            >
              {checking ? 'Checking…' : 'Check availability'}
            </button>
          ) : (
            <ConflictResolutionList
              slotsData={slotsData}
              unresolvedCount={unresolvedCount}
              onPickSuggested={pickSuggested}
              onSkip={skip}
              onUndo={undo}
              onEdit={edit}
              // PROD-2383 — no per-occurrence re-selection for a device (see
              // `RecurrenceActor`): both handlers are withheld, so neither
              // "See all" nor "Select another doctor" is rendered.
              onSeeAll={device ? undefined : openSeeAll}
              onSelectDoctor={device ? undefined : openSelectDoctor}
              unavailableCopy={device ? 'Device not available.' : 'Doctor not available.'}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** BridgeStatusChip semantic per Flutter `getSlotStatusColor` (widget 746-754):
 * Skipped → grey/neutral, Available → green/success, else → red/error. */
function statusSemantic(status: string): NonNullable<StatusChipProps['semantic']> {
  if (status === SLOT_STATUS_SKIPPED) return 'neutral';
  if (status === SLOT_STATUS_AVAILABLE) return 'success';
  return 'error';
}

interface ConflictResolutionListProps {
  slotsData: SlotsDataModel;
  unresolvedCount: number;
  onPickSuggested: (index: number, slot: AppointmentSlot) => void;
  onSkip: (index: number) => void;
  onUndo: (index: number) => void;
  onEdit: (index: number) => void;
  /** Omitted (device) → no "See all" affordance. */
  onSeeAll?: (index: number) => void;
  /** Omitted (device) → no "Select another doctor" affordance. */
  onSelectDoctor?: (index: number) => void;
  /** The line under an occurrence with no suggestion. */
  unavailableCopy: string;
}

/**
 * Per-occurrence conflict resolution — Flutter `availabilityConflictView`
 * (widget 702-744) + the three row builders (`notAvailableView`,
 * `availableSlotView`, `skippedView`). Occurrences already on the calendar
 * (`slot != null`) render nothing.
 */
function ConflictResolutionList({
  slotsData,
  unresolvedCount,
  onPickSuggested,
  onSkip,
  onUndo,
  onEdit,
  onSeeAll,
  onSelectDoctor,
  unavailableCopy,
}: ConflictResolutionListProps) {
  const details = slotsData.slotDetails ?? [];
  const bookedCount = countActiveOccurrences(slotsData);
  return (
    <div>
      <p className="text-xs font-medium text-brand-depth mb-1">
        Availability conflict
      </p>
      <p className="text-sm text-text-muted leading-snug mb-3">
        Some of the requested slots are not available. To resolve select one of the
        suggested available slots or Skip Occurrence completely.
      </p>
      {unresolvedCount > 0 ? (
        <p className="text-xs text-text-muted mb-3">
          {unresolvedCount}{' '}
          {unresolvedCount === 1 ? 'occurrence still needs' : 'occurrences still need'}{' '}
          resolving before you can save.
        </p>
      ) : bookedCount === 0 ? (
        // All occurrences skipped — booking zero slots is a no-op; Save stays
        // disabled (computeSaveEnabled requires ≥1 active). Tell the user why.
        <p className="text-xs font-semibold text-status-error mb-3">
          Every occurrence is skipped — un-skip at least one to save.
        </p>
      ) : (
        <p className="text-xs font-semibold text-green-700 mb-3">
          All conflicts resolved — {bookedCount}{' '}
          {bookedCount === 1 ? 'occurrence' : 'occurrences'} will book.
        </p>
      )}

      <div className="rounded-lg border border-border-default bg-surface-card divide-y divide-border-subtle">
        {details.map((detail, index) => {
          // Confirmed occurrence — nothing to resolve (Flutter renders Container()).
          if (detail.slot != null) return null;
          const status = effectiveSlotStatus(detail);
          return (
            <ConflictRow
              key={index}
              index={index}
              detail={detail}
              status={status}
              onPickSuggested={onPickSuggested}
              onSkip={onSkip}
              onUndo={onUndo}
              onEdit={onEdit}
              onSeeAll={onSeeAll}
              onSelectDoctor={onSelectDoctor}
              unavailableCopy={unavailableCopy}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ConflictRowProps {
  index: number;
  detail: SlotDetail;
  status: string;
  onPickSuggested: (index: number, slot: AppointmentSlot) => void;
  onSkip: (index: number) => void;
  onUndo: (index: number) => void;
  onEdit: (index: number) => void;
  onSeeAll?: (index: number) => void;
  onSelectDoctor?: (index: number) => void;
  unavailableCopy: string;
}

function ConflictRow({
  index,
  detail,
  status,
  onPickSuggested,
  onSkip,
  onUndo,
  onEdit,
  onSeeAll,
  onSelectDoctor,
  unavailableCopy,
}: ConflictRowProps) {
  const num = `#${index + 1}`;
  const chip = (
    <BridgeStatusChip tone="soft" semantic={statusSemantic(status)}>
      {status}
    </BridgeStatusChip>
  );

  // Skipped — strikethrough original time + Undo (Flutter skippedView 756-819).
  if (status === SLOT_STATUS_SKIPPED) {
    return (
      <div className="p-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-text-primary">{num}</span>
          <span className="text-sm font-bold text-text-primary line-through">
            {fmtDate(detail.appointmentTime)} at {fmtTime(detail.appointmentTime)}
          </span>
          <span className="ml-auto">{chip}</span>
        </div>
        <button
          type="button"
          onClick={() => onUndo(index)}
          className="mt-2 text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Undo
        </button>
      </div>
    );
  }

  // Resolved (picked or auto-suggested) — Flutter availableSlotView 821-953.
  if (status === SLOT_STATUS_AVAILABLE && detail.selectedSlot) {
    const doctorChanged = isDoctorChanged(detail);
    return (
      <div className="p-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-text-primary">{num}</span>
          <span className="text-sm font-bold text-text-primary">
            {fmtDate(detail.selectedSlot.start)} at {fmtTime(detail.selectedSlot.start)}
          </span>
          <span className="ml-auto">{chip}</span>
        </div>
        <p className="mt-1 text-sm italic text-text-muted line-through">
          {fmtDate(detail.appointmentTime)} at {fmtTime(detail.appointmentTime)}
        </p>
        {doctorChanged ? (
          <p className="mt-1 text-sm italic text-text-muted">Doctor has been changed.</p>
        ) : null}
        <div className="mt-2 flex items-center gap-4">
          {doctorChanged ? (
            // Doctor-changed slot — let the user re-pick another doctor
            // (PROD-1011) or skip. "Edit" (revert) isn't offered here because
            // there's no original same-doctor slot to revert to.
            <>
              {onSelectDoctor ? (
                <button
                  type="button"
                  onClick={() => onSelectDoctor(index)}
                  className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
                >
                  Select another doctor
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onSkip(index)}
                className="ml-auto text-sm font-medium text-status-error hover:opacity-80"
              >
                Skip this occurrence
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onEdit(index)}
              className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  }

  // Not available — Flutter notAvailableView 961-1116.
  const suggestions = detail.nearestSlots ?? [];
  const hasSuggestions = suggestions.length > 0;
  return (
    <div className="p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-text-primary">{num}</span>
        <span className="text-sm font-bold text-text-primary">
          {fmtDate(detail.appointmentTime)} at {fmtTime(detail.appointmentTime)}
        </span>
        <span className="ml-auto">{chip}</span>
      </div>
      <p className="mt-2 text-sm italic text-text-muted">
        {hasSuggestions ? 'Suggested next available slots:' : unavailableCopy}
      </p>
      {hasSuggestions ? (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPickSuggested(index, s)}
              className="h-8 rounded-full bg-grey-100 text-sm font-medium text-text-primary hover:bg-blue-100 hover:text-brand-primary transition-colors px-2 truncate"
            >
              {fmtDate(s.start)} {fmtTime(s.start)}
            </button>
          ))}
        </div>
      ) : null}
      <div className="mt-2 flex items-center gap-4">
        {/* PROD-1011 — re-enter slot selection. With suggestions: "See all"
            browses every time for the SAME doctor. Without: "Select another
            doctor" re-picks the provider. Either way, Skip stays available. */}
        {hasSuggestions ? (
          onSeeAll ? (
            <button
              type="button"
              onClick={() => onSeeAll(index)}
              className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
            >
              See all
            </button>
          ) : null
        ) : onSelectDoctor ? (
          <button
            type="button"
            onClick={() => onSelectDoctor(index)}
            className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
          >
            Select another doctor
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => onSkip(index)}
          className="ml-auto text-sm font-medium text-status-error hover:opacity-80"
        >
          Skip this occurrence
        </button>
      </div>
    </div>
  );
}

interface StepperProps {
  label: string;
  value: number;
  min?: number;
  onChange: (next: number) => void;
}

function Stepper({ label, value, min = 0, onChange }: StepperProps) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted mb-2">
        {label}
      </p>
      <div className="inline-flex items-center gap-3">
        <button
          type="button"
          onClick={() => value > min && onChange(value - 1)}
          disabled={value <= min}
          className={cn(
            'h-10 w-10 flex items-center justify-center rounded-full border',
            value > min
              ? 'border-border-default text-brand-primary hover:bg-blue-100'
              : 'border-border-subtle text-text-hint cursor-not-allowed',
          )}
          aria-label="Decrement"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="text-base font-semibold text-text-primary tabular-nums w-8 text-center">
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="h-10 w-10 flex items-center justify-center rounded-full border border-border-default text-brand-primary hover:bg-blue-100"
          aria-label="Increment"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
